import {identity} from 'ramda';
import {BehaviorSubject, Observable} from 'rxjs';

/**
 * The decorated Observable property will be replaced with a BehaviorSubject,
 * and newly assigned Observables will be passed through that BehaviorSubject.
 * To be specific, a subscription to the new Observable passed the values
 * to the backing BehaviorSubject.
 * Optionally, those streams can also be transformed before being passed along.
 *
 * @param initialValue What value should this Observable start with?
 * @param streamMap How should new streams be transformed? Default: identity
 * @returns A property decorator. Only apply this onto Observables!
 */
export function persistObservable<K>(
  initialValue: K,
  streamMap: (v: Observable<K>) => Observable<K> = identity
) {
  return function (target: unknown, propertyKey: string) {
    const persistentStreamKey = Symbol(`${propertyKey}PersistentStream`);
    const baseStreamKey = Symbol(`${propertyKey}BaseStream`);
    const subscription = Symbol(`${propertyKey}Subscription`);
    Object.defineProperty(target, propertyKey, {
      get() {
        return (this[persistentStreamKey] ??= new BehaviorSubject<K>(
          initialValue
        ));
      },
      set(v: Observable<K>) {
        this[baseStreamKey] = streamMap(v);
        this[subscription]?.unsubscribe();
        this[subscription] = this[baseStreamKey].subscribe(
          this[persistentStreamKey].next
        );
      },
    });
  };
}
